import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

function tagStyle(color, bg) {
  return { fontSize: '10px', padding: '1px 6px', borderRadius: '10px', background: bg, color, fontWeight: '500', marginLeft: '4px', flexShrink: 0 }
}

function sourceTag(notes) {
  if (!notes || notes.includes('card')) return null
  if (notes === 'from_play') return <span style={tagStyle('#3B6D11', '#EAF3DE')}>Play</span>
  if (notes === 'free') return <span style={tagStyle('#0F6E56', '#E1F5EE')}>Free</span>
  if (notes === 'sideline') return <span style={tagStyle('#533AB7', '#EEEDFE')}>Sideline</span>
  if (notes === '45' || notes === '65') return <span style={tagStyle('#854F0B', '#FAEEDA')}>{notes}</span>
  if (notes === 'penalty') return <span style={tagStyle('#A32D2D', '#FCEBEB')}>Penalty</span>
  return null
}

function SectionHeader({ title, subtitle, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
      <p style={{ fontSize: '10px', color, fontWeight: '600', letterSpacing: '0.5px' }}>{title}</p>
      {subtitle && <p style={{ fontSize: '10px', color: '#888' }}>{subtitle}</p>}
    </div>
  )
}

export default function EventTable({ events, fixture, sport, ourWides, oppWides, ourFortyFives, oppFortyFives, ourKOWon, ourKOLost, oppKOWon, oppKOLost, freesWon, freesConceded }) {
  const [oppPlayers, setOppPlayers] = useState([])
  useEffect(() => {
    if (!fixture?.id) return
    supabase.from('opposition_players').select('jersey_number, full_name').eq('fixture_id', fixture.id)
      .then(({ data }) => setOppPlayers(data || []))
  }, [fixture?.id])
  function getOppPlayerName(jerseyNum) {
    if (!jerseyNum) return null
    return oppPlayers.find(p => p.jersey_number === jerseyNum)?.full_name || null
  }
  const isHurl = fixture?.sport === 'hurling' || fixture?.sport === 'camogie'
  const koLabel = isHurl ? 'Puck Outs' : 'Kick Outs'
  const setPieceLabel = isHurl ? '65s' : '45s'

  if (events.length === 0) return (
    <p style={{ fontSize: '13px', color: '#bbb', textAlign: 'center', padding: '24px 0' }}>No events logged yet</p>
  )

  const goals = events.filter(e => ['goal', 'opp_goal'].includes(e.event_type))
  const points = events.filter(e => ['point', 'opp_point', 'twopoint', 'opp_twopoint'].includes(e.event_type))
  const wides = events.filter(e => ['wide', 'opp_wide', 'fortyfive_won', 'fortyfive_conceded'].includes(e.event_type))
  const kickouts = events.filter(e => ['kickout_won', 'kickout_lost', 'opp_kickout_won', 'opp_kickout_lost'].includes(e.event_type))
  const frees = events.filter(e => ['free_won', 'free_conceded'].includes(e.event_type))
  const cards = events.filter(e => e.event_type.includes('card') && !e.event_type.includes('undo'))
  const subs = events.filter(e => ['sub_on', 'sub_off'].includes(e.event_type))

  const ourGoalsFromPlay = goals.filter(e => e.event_type === 'goal' && e.notes === 'from_play').length
  const oppGoalsFromPlay = goals.filter(e => e.event_type === 'opp_goal' && e.notes === 'from_play').length
  const ourPointsFromPlay = points.filter(e => ['point', 'twopoint'].includes(e.event_type) && e.notes === 'from_play').length
  const oppPointsFromPlay = points.filter(e => ['opp_point', 'opp_twopoint'].includes(e.event_type) && e.notes === 'from_play').length

  let ourKOWonR = 0, ourKOLostR = 0, oppKOWonR = 0, oppKOLostR = 0, freesWonR = 0, freesConcededR = 0

  return (
    <div>
      {goals.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader
            title="GOALS"
            subtitle={'Us: ' + goals.filter(e => e.event_type === 'goal').length + ' (' + ourGoalsFromPlay + ' play) · Opp: ' + goals.filter(e => e.event_type === 'opp_goal').length + ' (' + oppGoalsFromPlay + ' play)'}
            color="#185FA5"
          />
          {goals.map(e => (
            <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5', alignItems: 'center' }}>
              <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
              <span style={{ flex: 1, color: '#333' }}>
                {e.event_type === 'goal' ? 'Our goal' : 'Opp goal'}
                {e.players?.full_name ? ' · ' + e.players.full_name : ''}
                {e.opp_jersey_number ? ' · #' + e.opp_jersey_number + (getOppPlayerName(e.opp_jersey_number) ? ' ' + getOppPlayerName(e.opp_jersey_number) : '') : ''}
              </span>
              {sourceTag(e.notes)}
            </div>
          ))}
        </div>
      )}

      {points.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader
            title="POINTS"
            subtitle={'Us: ' + points.filter(e => ['point','twopoint'].includes(e.event_type)).length + ' (' + ourPointsFromPlay + ' play) · Opp: ' + points.filter(e => ['opp_point','opp_twopoint'].includes(e.event_type)).length + ' (' + oppPointsFromPlay + ' play)'}
            color="#3B6D11"
          />
          {points.map(e => (
            <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5', alignItems: 'center' }}>
              <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
              <span style={{ flex: 1, color: '#333' }}>
                {e.event_type === 'point' ? 'Point' : e.event_type === 'twopoint' ? '2-Point' : e.event_type === 'opp_point' ? 'Opp point' : 'Opp 2-Point'}
                {e.players?.full_name ? ' · ' + e.players.full_name : ''}
                {e.opp_jersey_number ? ' · #' + e.opp_jersey_number + (getOppPlayerName(e.opp_jersey_number) ? ' ' + getOppPlayerName(e.opp_jersey_number) : '') : ''}
              </span>
              {sourceTag(e.notes)}
            </div>
          ))}
        </div>
      )}

      {wides.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader
            title={'WIDES & ' + setPieceLabel.toUpperCase()}
            subtitle={'Our wides: ' + ourWides + ' · Opp wides: ' + oppWides + ' · Our ' + setPieceLabel + ': ' + ourFortyFives + ' · Opp ' + setPieceLabel + ': ' + oppFortyFives}
            color="#854F0B"
          />
          {wides.map(e => (
            <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5', alignItems: 'center' }}>
              <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
              <span style={{ flex: 1, color: '#333' }}>
                {e.event_type === 'wide' ? 'Our wide' : e.event_type === 'opp_wide' ? 'Opp wide' : e.event_type === 'fortyfive_won' ? setPieceLabel.slice(0, -1) + ' won' : setPieceLabel.slice(0, -1) + ' conceded'}
                {e.players?.full_name ? ' · ' + e.players.full_name : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {kickouts.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader
            title={koLabel.toUpperCase()}
            subtitle={'Ours: ' + ourKOWon + 'W ' + ourKOLost + 'L · Opp: ' + oppKOWon + 'W ' + oppKOLost + 'L'}
            color="#3B6D11"
          />
          {kickouts.map(e => {
            if (e.event_type === 'kickout_won') ourKOWonR++
            else if (e.event_type === 'kickout_lost') ourKOLostR++
            else if (e.event_type === 'opp_kickout_won') oppKOWonR++
            else oppKOLostR++
            return (
              <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5' }}>
                <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
                <span style={{ flex: 1, color: '#333' }}>
                  {e.event_type.replace(/_/g, ' ')}
                  {e.players?.full_name ? ' · ' + e.players.full_name : ''}
                </span>
                <span style={{ fontSize: '11px', color: '#888', flexShrink: 0 }}>{ourKOWonR}W {ourKOLostR}L | {oppKOWonR}W {oppKOLostR}L</span>
              </div>
            )
          })}
        </div>
      )}

      {frees.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader
            title="FREES"
            subtitle={'Won: ' + freesWon + ' · Conceded: ' + freesConceded}
            color="#0F6E56"
          />
          {frees.map(e => {
            if (e.event_type === 'free_won') freesWonR++
            else freesConcededR++
            return (
              <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5' }}>
                <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
                <span style={{ flex: 1, color: '#333' }}>
                  {e.event_type === 'free_won' ? 'Free won' : 'Free conceded'}
                  {e.players?.full_name ? ' · ' + e.players.full_name : ''}
                  {e.notes === 'opp_fouled_ball' ? ' · Opp fouled ball' : ''}
                </span>
                <span style={{ fontSize: '11px', color: '#888', flexShrink: 0 }}>Won: {freesWonR} · Con: {freesConcededR}</span>
              </div>
            )
          })}
        </div>
      )}

      {cards.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader title="CARDS" color="#854F0B" />
          {cards.map(e => (
            <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5' }}>
              <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
              <span style={{ flex: 1, color: '#333' }}>
                {e.event_type.replace(/_/g, ' ')}
                {e.players?.full_name ? ' · ' + e.players.full_name : ''}
                {e.notes ? ' · ' + e.notes : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {subs.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionHeader title="SUBSTITUTIONS" color="#533AB7" />
          {subs.map(e => (
            <div key={e.id} style={{ display: 'flex', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5' }}>
              <span style={{ color: '#aaa', minWidth: '28px', flexShrink: 0 }}>{e.minute}'</span>
              <span style={{ flex: 1, color: '#333' }}>
                {e.event_type.replace(/_/g, ' ')}
                {e.players?.full_name ? ' · ' + e.players.full_name : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}